import React, { memo } from 'react';
import { strTrim, hasDefeitoAlert, normalizeHexColor, defeitoAlertTooltipText } from '../utils/defeitoAlertBadge';
import { getBoardClienteExibicao, getBoardClienteTooltipNome } from '../utils/clienteBoardDisplay';
import { resolveBoardDragOrderIds, setBoardOrderDragDataTransfer, clearBoardOrderDragSource } from '../utils/boardOrderDrag';
import { areBoardOrderRowPropsEqual } from '../utils/boardOrderRowCompare';

function getSLAColor(sla) {
  switch (sla) {
    case 'Vencido':
    case 'vencido':
      return '#ef5350';
    case 'Vencendo':
    case 'vencendo':
      return '#ff9800';
    default:
      return 'transparent';
  }
}

function getServiceColor(tipo) {
  switch (tipo) {
    case 'E':
      return '#9c27b0';
    case 'B':
      return '#ff5722';
    case 'A':
      return '#607d8b';
    case 'R':
      return '#795548';
    case 'D':
      return '#f44336';
    case 'I':
      return '#2196f3';
    case 'S':
      return '#4caf50';
    case 'C':
      return '#ff9800';
    default:
      return '#9e9e9e';
  }
}

function OpenColumnOrderRow({
  ordem,
  isStatusRetorno,
  selectedOrders,
  onOrderClick,
  onSetContextMenu,
  onDragStartEnsureSelection,
  onBoardOrderDragStart,
  onBoardOrderDragEnd,
  userShowOSConfig,
  onBoardCellTooltip,
  showStatusColumnEmAberto,
}) {
  const defeitoAlert = hasDefeitoAlert(ordem);
  const defeitoBg = defeitoAlert ? normalizeHexColor(ordem.color_bg) : null;
  const slaColor = getSLAColor(ordem.sla);
  const equipamentoLabel =
    ordem.equipamento || ordem.TB01010_RESUMIDO || ordem.TB01010_NOME || '';

  return (
    <div
      className={`order-row ${selectedOrders.includes(ordem.id) ? 'selected' : ''} ${
        isStatusRetorno ? 'order-status-retorno' : ''
      } ${ordem.pedidoVinculado ? 'has-pedido-vinculado' : ''}${
        defeitoAlert ? ' order-row--defeito-alert' : ''
      }`}
      onClick={() => onOrderClick(ordem)}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSetContextMenu({
          x: e.clientX,
          y: e.clientY,
          orderId: ordem.id,
          source: 'open',
        });
      }}
      draggable
      onDragStart={(e) => {
        const idsToDrag = resolveBoardDragOrderIds(selectedOrders, ordem.id);
        setBoardOrderDragDataTransfer(e.dataTransfer, ordem.id, idsToDrag);
        clearBoardOrderDragSource();
        const alreadySelected = selectedOrders.some((id) => String(id) === String(ordem.id));
        if (!alreadySelected) {
          onDragStartEnsureSelection(ordem.id);
        }
        onBoardOrderDragStart?.();
      }}
      onDragEnd={() => {
        clearBoardOrderDragSource();
        onBoardOrderDragEnd?.();
      }}
    >
      <div className="order-cell order-id">
        <span
          className={`service-type-badge-compact${
            defeitoAlert ? ' service-type-badge-compact--defeito-alert board-cell-tooltip-trigger' : ''
          }`}
          style={{
            backgroundColor: defeitoBg || getServiceColor(ordem.tipo),
          }}
          onMouseEnter={
            defeitoAlert
              ? (e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  onBoardCellTooltip({
                    text: defeitoAlertTooltipText(ordem),
                    x: r.left + r.width / 2,
                    y: r.bottom - 4,
                    triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
                  });
                }
              : undefined
          }
          onMouseLeave={defeitoAlert ? () => onBoardCellTooltip(null) : undefined}
        >
          {defeitoAlert ? strTrim(ordem.defeito_alert) : ordem.tipo || '?'}
        </span>
        {slaColor !== 'transparent' ? (
          <div className="sla-indicator" style={{ backgroundColor: slaColor }} />
        ) : null}
        <span
          className="order-id-text board-cell-tooltip-trigger"
          onMouseEnter={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const text =
              userShowOSConfig === 1
                ? `Número da OS: ${ordem.TB02115_CODIGO || ordem.id}\nNúmero de série: ${
                    ordem.TB02115_NUMSERIE || ordem.numeroSerie || 'N/A'
                  }`
                : `Número de patrimônio: ${ordem.TB02112_PAT || ordem.patrimonio || 'N/A'}\nNúmero de série: ${
                    ordem.TB02115_NUMSERIE || ordem.numeroSerie || 'N/A'
                  }`;
            onBoardCellTooltip({
              text,
              x: r.left + r.width / 2,
              y: r.bottom - 4,
              triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
            });
          }}
          onMouseLeave={() => onBoardCellTooltip(null)}
        >
          {userShowOSConfig === 1
            ? ordem.TB02112_PAT || ordem.patrimonio || ordem.id
            : ordem.id}
        </span>
        {isStatusRetorno && (
          <span
            className="order-retorno-icon-wrap board-cell-tooltip-trigger"
            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, lineHeight: 1 }}
            onMouseEnter={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              onBoardCellTooltip({
                text: 'Retorno',
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }}
            onMouseLeave={() => onBoardCellTooltip(null)}
          >
            <i className="bi bi-bell order-retorno-icon" style={{ fontSize: '10px' }} aria-hidden />
          </span>
        )}
        {ordem.pedidoVinculado && (
          <span
            className="board-cell-tooltip-trigger order-pedido-vinc-icon-wrap"
            style={{ display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={(e) => {
              const text = `Pedido vinculado: ${ordem.pedidoVinculado}`;
              const r = e.currentTarget.getBoundingClientRect();
              onBoardCellTooltip({
                text,
                x: r.left + r.width / 2,
                y: r.bottom - 4,
                triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
              });
            }}
            onMouseLeave={() => onBoardCellTooltip(null)}
          >
            <i className="bi bi-box-fill pedido-vinculado-icon" style={{ fontSize: '10px' }} aria-hidden />
          </span>
        )}
        {showStatusColumnEmAberto && (
          <span
            className="order-status-col"
            title={`Status: ${ordem.TB02115_STATUS || ''}`}
            style={{
              fontSize: '9px',
              fontWeight: 600,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            }}
          >
            {String(ordem.TB02115_STATUS || '')
              .trim()
              .slice(0, 2)
              .padEnd(2, '\u00A0')}
          </span>
        )}
      </div>
      <div
        className="order-cell order-cliente board-cell-tooltip-trigger"
        onMouseEnter={(e) => {
          const tip = getBoardClienteTooltipNome(ordem);
          if (tip) {
            const r = e.currentTarget.getBoundingClientRect();
            onBoardCellTooltip({
              text: tip,
              x: r.left + r.width / 2,
              y: r.bottom - 4,
              triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
            });
          }
        }}
        onMouseLeave={() => onBoardCellTooltip(null)}
      >
        {getBoardClienteExibicao(ordem)}
      </div>
      <div
        className="order-cell order-equipamento board-cell-tooltip-trigger"
        onMouseEnter={(e) => {
          if (equipamentoLabel) {
            const r = e.currentTarget.getBoundingClientRect();
            onBoardCellTooltip({
              text: equipamentoLabel,
              x: r.left + r.width / 2,
              y: r.bottom - 4,
              triggerRect: { left: r.left, top: r.top, width: r.width, height: r.height },
            });
          }
        }}
        onMouseLeave={() => onBoardCellTooltip(null)}
      >
        {equipamentoLabel}
      </div>
    </div>
  );
}

const OpenColumnOrderRowMemo = memo(OpenColumnOrderRow, areBoardOrderRowPropsEqual);
OpenColumnOrderRowMemo.displayName = 'OpenColumnOrderRow';

export default OpenColumnOrderRowMemo;
